import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  Chip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ScienceIcon from '@mui/icons-material/Science';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const MANUAL_SECTIONS = [
  {
    id: 'm-01',
    code: 'SEC 01',
    badge: 'Platform Architecture',
    title: 'Platform Overview & 11-Module Computational Discovery Suite',
    summary: 'Unified Computer-Aided Drug Design (CADD), Cheminformatics, Structural Biology, and Multi-Parametric Lead Optimization workbench.',
    keywords: 'overview modules dashboard similarity ligtmap omegafold alphafold hmdb dynadock vina admet molViz solpred drugex dossier',
    type: 'table',
    body: [
      'DiscoveraAI™ (v4.0 Enterprise Discovery Suite) unifies ligand-based drug design (LBDD), structure-based drug design (SBDD), ab initio protein folding, multi-task ADMET prediction, thermodynamic solubility modeling, and Pareto generative reinforcement learning into a single web workbench.',
      'Use the left navigation sidebar or Topbar controls to seamlessly pass SMILES, SDF libraries, and 3D PDB coordinates across 11 specialized computational engines:'
    ],
    tableRows: [
      ['1. Dashboard', '/home', 'HPC cluster job telemetry, unified task queue monitor & quick-launch cards across Docking, DTI, PSP & ADMET'],
      ['2. Similarity Search', '/similarity', 'High-throughput 2D/3D Morgan ECFP4 & MACCS Tanimoto chemical similarity and target screening'],
      ['3. TargetPred (LigTMap)', '/target-prediction/', 'Hybrid ligand- & structure-based Drug-Target Interaction (DTI) prediction across human protein classes'],
      ['4. ProteinPred', '/protein-struct-prediction', 'OmegaFold / AlphaFold ab initio 3D tertiary protein folding from primary FASTA sequences with pLDDT confidence'],
      ['5. HMDB Explorer', '/hmdb', 'Human Metabolome Database endogenous metabolite, biofluid & UniProt target search engine'],
      ['6. Molecular Dock', '/dock', 'DynaDock / AutoDock Vina 3D receptor-ligand docking computing ΔG (kcal/mol), Ki & binding poses'],
      ['7. ADMET AI', '/admet', 'SpectraRank 40+ endpoint PK, CYP450, hERG toxicity, QED, SAscore & Lipinski/Veber medicinal chemistry profiler'],
      ['8. MolViz Studio', '/MolViz', 'Interactive NGL WebGL 3D macromolecular & ligand complex visualizer (PDB, SDF, MOL2, surface/cartoon)'],
      ['9. SolPred (Enformis)', '/SolPred', 'Thermodynamic aqueous & multi-solvent Abraham solvation solubility predictor for formulation design'],
      ['10. DrugEx De Novo', '/Drugex', 'Deep generative de novo molecular design with Pareto multi-objective reinforcement learning'],
      ['11. 360° Dossier', '/dossier', 'Direct bridge to the autonomous Dossier360 multi-agent clinical, IP/FTO & financial valuation platform']
    ]
  },
  {
    id: 'm-02',
    code: 'SEC 02',
    badge: 'Cheminformatics & Screening',
    title: 'Dashboard (/home), Similarity Search (/similarity) & HMDB Explorer (/hmdb)',
    summary: 'Step-by-step operations for monitoring asynchronous HPC queues, drawing 2D structures in Kekule.js, and screening chemical/metabolomic libraries.',
    keywords: 'dashboard similarity search kekule 2d sketcher smiles tanimoto hmdb uniprot metabolite',
    body: [
      '• Discovery Command Dashboard (/home): Displays real-time counters and task tables across Molecular Docking (dock), Drug Target Prediction (ligtmap/dtp), Protein Structure Prediction (omega/psp), and ADMET AI. Click any completed job row to inspect or export results.',
      '• Similarity Search (/similarity): Draw a query structure using the interactive Kekule.js 2D chemical editor or paste a canonical SMILES string. Select the target screening database (ChEMBL, ZINC, HMDB, Approved Drugs) and Tanimoto threshold (0.40–1.00) to retrieve structural analogs and scaffold hops in AG Grid.',
      '• HMDB Metabolomic Search (/hmdb): Query by UniProt ID (e.g., P00533, P14416) or metabolite descriptor to inspect endogenous human metabolites, biofluid concentrations, and metabolic pathway associations.'
    ]
  },
  {
    id: 'm-03',
    code: 'SEC 03',
    badge: 'Structural Biology & SBDD',
    title: 'TargetPred LigTMap (/target-prediction), ProteinPred (/protein-struct-prediction), Molecular Dock (/dock) & MolViz (/MolViz)',
    summary: 'Operating the target prediction pipeline, FASTA-to-PDB folding, 3D binding-pocket docking, and NGL WebGL macromolecular visualization.',
    keywords: 'target prediction ligtmap protein structure prediction fasta pdb docking autodock vina dynadock delta g rmsd molviz ngl',
    body: [
      '• TargetPred — LigTMap (/target-prediction/): Submit single or batch SMILES strings and select target protein classes (Kinases, GPCRs, Proteases, Ion Channels, Nuclear Receptors). LigTMap outputs ranked UniProt/PDB targets with predicted binding affinity scores (pKd / pIC50) and ligand/binding-site similarity metrics.',
      '• ProteinPred — OmegaFold / AlphaFold (/protein-struct-prediction): Paste a primary amino-acid sequence in FASTA format to fold uncharacterized targets or mutant variants into atomic 3D PDB coordinates colored by per-residue pLDDT confidence (0–100).',
      '• Molecular Dock — DynaDock / AutoDock Vina (/dock): Upload or select a receptor PDB structure, configure the 3D active-site grid box center (x, y, z) and dimensions (Å), and provide ligand SMILES or SDF files. Inspect ranked binding modes by Gibbs free energy of binding (ΔG in kcal/mol), RMSD (Å), and non-covalent interactions (H-bonds, π-π stacking, hydrophobic contacts).',
      '• MolViz 3D Studio (/MolViz): Load any 4-character RCSB PDB accession or local .pdb/.sdf/.mol2 file into the NGL WebGL Stage. Switch representations (Cartoon, Ball+Stick, Licorice, Surface, Ribbon), color by hydrophobicity, B-factor, or secondary structure, and export high-resolution PNG figures.'
    ]
  },
  {
    id: 'm-04',
    code: 'SEC 04',
    badge: 'Lead Optimization & De Novo AI',
    title: 'SpectraRank ADMET AI (/admet), Enformis SolPred (/SolPred), DrugEx (/Drugex) & 360° Dossier (/dossier)',
    summary: 'Profiling 40+ ADMET/Tox endpoints, thermodynamic multi-solvent solubility, Pareto de novo generation, and automated BD&L dossier compilation.',
    keywords: 'admet spectrarank lipinski veber qed sascore herg cyp450 bbb solpred enformis solubility drugex pareto generative dossier',
    body: [
      '• SpectraRank ADMET AI (/admet): Input molecules via the 2D sketcher, SMILES list, or CSV/SDF upload to compute 40+ endpoints across Physicochemical Properties (MW, cLogP, LogD, TPSA, HBD, HBA, Rotatable Bonds), Medicinal Chemistry Rules (QED, SAscore, Lipinski Rule of 5, Veber, Egan, Muegge, PAINS, Brenk), Absorption (Caco-2, HIA, P-gp, F20%/F30%), Distribution (PPB%, Vdss, BBB Permeance), Metabolism (CYP1A2, CYP2C9, CYP2C19, CYP2D6, CYP3A4 inhibition/substrate), Excretion (CL, t1/2), and Toxicity (hERG Cardiotoxicity, Ames Mutagenicity, DILI, Hepatotoxicity). Use the interactive Radar Chart and parameter sliders to filter hits.',
      '• Enformis SolPred (/SolPred): Predict intrinsic aqueous solubility (log S0) and temperature-dependent organic/co-solvent solubility (Ethanol, DMSO, PEG-400, Methanol, Ethyl Acetate) to guide salt/polymorph screening and BCS formulation design.',
      '• DrugEx De Novo Design (/Drugex): Provide a seed scaffold or fragment and launch multi-objective Pareto reinforcement learning to generate novel, valid, synthetically accessible chemical matter optimized simultaneously for target potency, selectivity, QED, and ADMET safety.',
      '• 360° Dossier Bridge (/dossier): Launch full multi-agent clinical, CMC, patent FTO, and 10-year financial NPV due diligence on any lead candidate directly inside DiscoveraAI.'
    ]
  }
];

const WHITEPAPER_SECTIONS = [
  {
    id: 'w-01',
    code: 'WP 01',
    badge: 'Cheminformatics & DTI Math',
    title: 'Morgan Circular Fingerprints (ECFP4), Tanimoto Indexing & LigTMap Hybrid DTI Formalism',
    summary: 'Mathematical formulation of iterative Morgan neighborhood hashing, inverted bit-index Tanimoto bounds, and hybrid ligand/pocket affinity regression.',
    keywords: 'ecfp4 morgan circular fingerprints tanimoto jaccard similarity ligtmap dti pkd pic50',
    body: [
      'DiscoveraAI canonicalizes input chemical graphs G = (V_atoms, E_bonds) via RDKit and encodes topological environments of diameter 4 (Morgan radius R = 2) into 2048-bit vectors fp(M) ∈ {0,1}^2048. Sub-linear screening across multi-million compound libraries leverages bit-population bounds prior to exact Tanimoto evaluation, while LigTMap combines 2D/3D ligand similarity with active-site pocket alignment and deep neural affinity regression:'
    ],
    codeBlock: `// 1. Morgan Iterative Neighborhood Hashing (r = 0, 1, 2)
I_i^(r) = HASH( I_i^(r-1), { (b_ij, I_j^(r-1)) | j ∈ N(i) } )   --> folded to fp(M) ∈ {0, 1}^2048

// 2. Tanimoto (Jaccard) Similarity & Bit-Count Pruning Bound
T_c(a, b) = (a · b) / ( ||a||_1 + ||b||_1 - a · b )  ≤  min(||a||_1, ||b||_1) / max(||a||_1, ||b||_1)

// 3. LigTMap Hybrid Drug-Target Interaction Score & Affinity
Score_LigTMap(M_q, P_t) = λ_1 · S_ligand(M_q, L(P_t)) + λ_2 · S_pocket(P_t) + λ_3 · ŷ_DNN(M_q, P_t)
where ŷ_DNN predicts pK_d = -log_10( K_d / 1 M )`
  },
  {
    id: 'w-02',
    code: 'WP 02',
    badge: 'Structural Biology & Physics Docking',
    title: 'Ab Initio PLM Folding (OmegaFold/AlphaFold) & Physics-Informed 3D Docking (DynaDock/Vina)',
    summary: 'Triangle multiplicative pairwise attention, SE(3)-equivariant Invariant Point Attention, empirical free-energy potentials, and BFGS torsional optimization.',
    keywords: 'omegafold alphafold plddt se3 equivariant fape autodock vina dynadock free energy delta g bfgs',
    body: [
      'For targets lacking experimental crystal structures, ProteinPred extracts single-residue embeddings and pairwise interaction tensors enforcing triangle multiplicative updates, followed by an SE(3)-equivariant structure module supervised by Frame Aligned Point Error (FAPE).',
      'In Molecular Dock (/dock), receptor-ligand conformations q = (t, r, θ) ∈ ℝ^(3 + 4 + N_rot) are optimized via Markov Chain Monte Carlo sampling paired with analytical-gradient BFGS quasi-Newton local refinement over empirical steric, hydrophobic, and H-bond potentials:'
    ],
    codeBlock: `// 1. Triangle Multiplicative Pairwise Update & Per-Residue Confidence
p_ij  <--  p_ij + Σ_(k=1..N_res) ( g_ik ⊙ h_jk ) ,     X_3D = SE3_StructureModule(z_i, p_ij)  with pLDDT ∈ [0, 100]

// 2. Intermolecular & Intramolecular Docking Potential E(q) over Surface Distance d_ij = r_ij - R_i^vdW - R_j^vdW
E(q) = Σ_(i < j) [ w_1·Gauss_1(d_ij) + w_2·Gauss_2(d_ij) + w_3·Repulsion(d_ij) + w_4·Hydrophobic(d_ij) + w_5·HBond(d_ij) ]

// 3. Conformational Entropy Scaling & Gibbs Free Energy of Binding (ΔG_bind)
ΔG_bind = E_inter(q*) / ( 1 + w_rot · N_rot ) = R·T·ln(K_i)   ==>   K_i = exp( ΔG_bind / (R·T) )`
  },
  {
    id: 'w-03',
    code: 'WP 03',
    badge: 'ADMET MPNN, SolPred & Generative RL',
    title: 'Directed MPNN ADMET (SpectraRank), Abraham Solvation (SolPred) & Pareto RL (DrugEx)',
    summary: 'Directed bond message passing, Bickerton QED desirability functions, Abraham Linear Free Energy solvation, and multi-objective Pareto frontier reinforcement learning.',
    keywords: 'dmpnn chemprop spectrarank admet qed bickerton abraham solvation solpred drugex pareto reinforcement learning',
    body: [
      'SpectraRank ADMET encodes molecular graphs via Directed Message-Passing Neural Networks (D-MPNN) over directed bond edges e_vw to prevent tottering walks, concatenating learned graph readouts with 200 RDKit physicochemical descriptors across 40+ multi-task heads.',
      'Enformis SolPred models crystal lattice dissociation and Abraham linear solvation free energy across diverse pharmaceutical solvents, while DrugEx trains autoregressive generative policy networks via Pareto-frontier ranking:'
    ],
    codeBlock: `// 1. Directed Message-Passing Neural Network (D-MPNN) Bond Update
h_vw^(t+1) = τ( W_i [ x_v || e_vw ] + W_h Σ_{k ∈ N(v)\\w} h_kv^(t) ) ,    h_M = [ Σ_{v,w} h_vw^(T) || d_RDKit ]

// 2. Bickerton Quantitative Estimate of Drug-Likeness (QED)
QED = exp( [ Σ_(k=1..8) w_k · ln d_k(x_k) ] / [ Σ_(k=1..8) w_k ] )   ∈ (0, 1]

// 3. Enformis Abraham Linear Solvation & Crystal Lattice Equation (SolPred)
log S_solvent = c + e·E + s·S + a·A + b·B + v·V - λ_cryst(T_m, T)

// 4. DrugEx Multi-Objective Pareto Dominance & Policy Gradient
M_a ≻ M_b  <==>  ∀k ∈ {1..K}: f_k(M_a) ≥ f_k(M_b)  AND  ∃k*: f_k*(M_a) > f_k*(M_b)
∇_θ J(θ) = E_{M ~ P_θ} [ R_Pareto(M) · ∇_θ log P_θ(M) ]`
  }
];

export const DocsPage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const activeSections = tabIndex === 0 ? MANUAL_SECTIONS : WHITEPAPER_SECTIONS;

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return activeSections;
    const q = searchQuery.toLowerCase();
    return activeSections.filter(
      (sec) =>
        sec.title.toLowerCase().includes(q) ||
        sec.summary.toLowerCase().includes(q) ||
        sec.keywords.toLowerCase().includes(q) ||
        sec.body.some((p) => p.toLowerCase().includes(q))
    );
  }, [activeSections, searchQuery]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ pb: 8, pt: 1 }}>
      {/* Executive Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0b192c 0%, #16325b 60%, #1e4276 100%)',
          color: '#ffffff',
          border: '1px solid rgba(255,255,255,0.12)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ maxWidth: 780 }}>
            <Chip
              label="DISCOVERAAI™ v4.0 • ENTERPRISE DOCUMENTATION & SCIENTIFIC WHITEPAPER"
              size="small"
              sx={{
                bgcolor: 'rgba(56, 189, 248, 0.18)',
                color: '#7dd3fc',
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: 1,
                mb: 1.5
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.5px' }}>
              {tabIndex === 0
                ? 'DiscoveraAI™ Comprehensive User Manual'
                : 'DiscoveraAI™ Technical & Scientific Whitepaper'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.65 }}>
              {tabIndex === 0
                ? 'Complete operational guide across all 11 computational drug discovery modules: Similarity Search, LigTMap Target Prediction, OmegaFold/AlphaFold Protein Folding, HMDB Metabolomics, DynaDock/Vina 3D Docking, SpectraRank ADMET AI, MolViz NGL Studio, Enformis SolPred, DrugEx Pareto De Novo Design, and 360° Dossier.'
                : 'Formal mathematical and systems reference detailing Morgan ECFP4 circular fingerprints, LigTMap hybrid DTI regression, SE(3)-equivariant protein structure folding, physics-informed docking free-energy scoring, Directed MPNN ADMET, Abraham solvation thermodynamics, and Pareto reinforcement learning.'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/home')}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => {
                const md = `# DISCOVERAAI™ v4.0 — ENTERPRISE USER & OPERATIONS MANUAL (CONFIDENTIAL & PROPRIETARY)\n\n` +
                  MANUAL_SECTIONS.map((s) =>
                    `## ${s.code}: ${s.title} [${s.badge}]\n${s.summary}\n\n` +
                    (s.body || []).join('\n\n')
                  ).join('\n\n')
                const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'DISCOVERAAI_USER_MANUAL.md'
                a.click()
                URL.revokeObjectURL(url)
              }}
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ bgcolor: '#0284c7', textTransform: 'none', fontWeight: 700 }}
            >
              Manual (.MD)
            </Button>
            <Button
              onClick={() => {
                const md = `# DISCOVERAAI™ v4.0 — TECHNICAL & SCIENTIFIC WHITEPAPER (CONFIDENTIAL & PROPRIETARY)\n\n` +
                  WHITEPAPER_SECTIONS.map((s) =>
                    `## ${s.code}: ${s.title} [${s.badge}]\n${s.summary}\n\n` +
                    (s.body || []).join('\n\n') +
                    (s.formulaBlock ? `\n\n\`\`\`\n${s.formulaBlock}\n\`\`\`` : '')
                  ).join('\n\n')
                const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'DISCOVERAAI_WHITEPAPER.md'
                a.click()
                URL.revokeObjectURL(url)
              }}
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ bgcolor: '#0d9488', textTransform: 'none', fontWeight: 700 }}
            >
              Whitepaper (.MD)
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Print / PDF
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.15)' }} />

        {/* Mode Switcher & Search */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            textColor="inherit"
            TabIndicatorProps={{ sx: { bgcolor: '#38bdf8', height: 3 } }}
          >
            <Tab
              icon={<MenuBookIcon fontSize="small" />}
              iconPosition="start"
              label="User & Operations Manual"
              sx={{ fontWeight: 700, textTransform: 'none', minHeight: 44 }}
            />
            <Tab
              icon={<ScienceIcon fontSize="small" />}
              iconPosition="start"
              label="Technical & Scientific Whitepaper"
              sx={{ fontWeight: 700, textTransform: 'none', minHeight: 44 }}
            />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search modules, equations, Vina, ADMET, LigTMap, DrugEx..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 340 },
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 1.5
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Paper>

      {/* Content Sections */}
      <Grid container spacing={3}>
        {filteredSections.map((sec) => (
          <Grid item xs={12} key={sec.id}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 2.5,
                border: '1px solid #e2e8f0',
                borderLeft: '5px solid #16325b'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Chip
                    label={sec.code}
                    size="small"
                    sx={{ bgcolor: '#16325b', color: '#fff', fontWeight: 800, fontFamily: 'monospace' }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    {sec.title}
                  </Typography>
                </Box>
                <Chip
                  label={sec.badge}
                  size="small"
                  sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', mb: 2 }}>
                {sec.summary}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {sec.body.map((paragraph, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: '#334155', lineHeight: 1.7 }}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>

              {sec.tableRows && (
                <TableContainer component={Paper} elevation={0} sx={{ mt: 2.5, border: '1px solid #e2e8f0' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                      <TableRow>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Module Name</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Route</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Scientific Function &amp; Computational Engine</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sec.tableRows.map((row, rIdx) => (
                        <TableRow key={rIdx} hover>
                          <TableCell sx={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{row[0]}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: '#0284c7', fontWeight: 600 }}>{row[1]}</TableCell>
                          <TableCell sx={{ color: '#334155', fontSize: '0.82rem' }}>{row[2]}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {sec.codeBlock && (
                <Box
                  component="pre"
                  sx={{
                    mt: 2.5,
                    p: 2.5,
                    bgcolor: '#0f172a',
                    color: '#38bdf8',
                    borderRadius: 2,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    overflowX: 'auto',
                    lineHeight: 1.6
                  }}
                >
                  {sec.codeBlock}
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DocsPage;
